/**
 * AnalyticsScanStatusToolbarFilter — SCAN outcome, promoted from a map-card-local control
 * to a real page-level filter (VERA-260).
 *
 * The control's anatomy is documented once, on `AnalyticsChipToolbarFilter`, which this
 * and `AnalyticsStatusToolbarFilter` both call. What is specific to this facet:
 *
 * ## It is a SECOND status axis, and it must never merge with the first
 *
 * A CERTIFICATE is Created / Active / Lost / Stolen; a SCAN is Verified / Invalid /
 * Opened. The two are orthogonal — an invalid scan of an active certificate is the single
 * most interesting row on this page, and it satisfies "Active" and "Invalid"
 * simultaneously. Put both vocabularies in one multi-select and selecting them together
 * has no defensible meaning: as an AND it is the useful query, as an OR it is a union of
 * two unrelated sets, and the control cannot say which it did. Two controls, ANDed, is the
 * only arrangement that expresses either.
 *
 * That is also why the two sit IMMEDIATELY BESIDE each other and why neither is labelled a
 * bare "Status" (§44) — adjacency is what makes the pairing legible, and adjacency is
 * exactly what makes an unqualified noun ambiguous.
 *
 * ## What it narrows today — stated plainly, because it is one card short of the page
 *
 * Server-side it reaches the scans-map, the KPI tiles, the …-by-time chart, the
 * leaderboard and the choropleth — every endpoint whose scan figure is counted from the
 * `scans` table, whose `invalid` / `tampered` columns the outcome resolves from.
 *
 * ⚠️ It reached the SCAN-MAP ALONE when this note was written, because the tiles, the
 * chart and the ROI figures all counted `certificate_action_logs` rows. VRG-452
 * (Scans.Data.Source.Divergence) moved the tiles' and the chart's scan counts onto
 * `scans`, and the filter followed.
 *
 * ⚠️ THE ROI FIGURES ARE STILL EXCLUDED, and that is structural rather than pending.
 * `/roi` is mixed: consumer touchpoints, repeat-scan rate and product lifespan remain on
 * `certificate_action_logs`, a table with no scan-outcome columns and no row-level link
 * back to a `scans` row, so there is no predicate it could honour that would not be an
 * invented semantic — and narrowing SOME of one card's figures is worse than narrowing
 * none. `/roi` does not declare the query prop at all. The full argument, including why
 * the available `EXISTS` approximation is much worse here than it is for geo, is on
 * `getScanMapPoints` in `analytics.postgres.repository.ts`.
 *
 * `certificateStatuses` still has the narrower reach this used to have — the totals
 * endpoint does not accept it — so that gap outlived this one.
 *
 * ## Why `ScanLineIcon`
 *
 * Not `NfcIcon`, which the toolbar's own search-scan button already carries an inch to the
 * left — two identical glyphs in one band, one meaning "tap a tag to search" and the other
 * "filter by scan outcome", is worse than no glyph. `CircleDotIcon` belongs to the
 * certificate-status control beside it for the same reason.
 */
import React from 'react'

import { ScanLineIcon } from 'lucide-react'

import { AnalyticsChipToolbarFilter } from './AnalyticsChipToolbarFilter'
import { FILTER_FIELDS, SCAN_STATUSES, SCAN_STATUS_OPTIONS } from './analyticsFilterFields'

interface AnalyticsScanStatusToolbarFilterProps {
  /** `filters.scanStatuses` verbatim — unlike the certificate axis there is no
   *  collapse/expand step, because no two scan outcomes share a label. */
  value: string[]
  onChange: (next: string[]) => void
}

export const AnalyticsScanStatusToolbarFilter = ({
  value,
  onChange,
}: AnalyticsScanStatusToolbarFilterProps) => (
  <AnalyticsChipToolbarFilter
    label="Scan status"
    icon={ScanLineIcon}
    options={SCAN_STATUS_OPTIONS}
    statuses={SCAN_STATUSES}
    popoverTitle="Scan Status"
    field={FILTER_FIELDS.scanStatus}
    value={value}
    onChange={onChange}
  />
)

export default AnalyticsScanStatusToolbarFilter
